import { useEffect } from "react";
import PolicyLayout from "../components/PolicyLayout";

/**
 * ChatPrivacyPage — public privacy policy for the Cleany Chat app.
 * Served at /chat/privacy so the mobile chat app (a FluffyChat fork)
 * can link back to a canonical URL. Content is a 1:1 rendering of
 * PRIVACY.md that ships with the chat codebase.
 */
export default function ChatPrivacyPage() {
    useEffect(() => {
        document.title = "Privatlivspolitik for Cleany Chat · Cleany.dk";
    }, []);

    return (
        <PolicyLayout title="Privatlivspolitik for Cleany Chat" lastUpdated="28. august 2026">
            <p className="lead">
                <strong>Den gældende version står på{" "}
                <a href="https://cleany.dk/chat/privacy">cleany.dk/chat/privacy</a>.</strong>{" "}
                Denne kopi følger med koden, så det fremgår af selve kildekoden hvad appen
                gør ved data. Er de to uenige, gælder siden på cleany.dk.
            </p>
            <p>
                Cleany Chat er en fork af{" "}
                <a href="https://github.com/krille-chan/fluffychat" target="_blank" rel="noopener noreferrer">
                    FluffyChat
                </a>
                . Upstreams egen privatlivspolitik gælder ikke her: FluffyChat kan
                forbindes til en hvilken som helst Matrix-server, hvorimod Cleany Chat
                kun taler med Cleanys egen — og det er derfor Cleany, ikke brugeren,
                der bestemmer hvad der sker med data.
            </p>

            <hr />

            <h2>Hvem er ansvarlig</h2>
            <p>
                Cleany Chat stilles til rådighed af Cleany ApS. Din konto oprettes af din
                arbejdsgiver eller den virksomhed, du samarbejder med gennem Cleany —
                ikke af dig selv.
            </p>
            <p>
                <strong>Din arbejdsgiver er dataansvarlig</strong> for samtalerne. Cleany
                er databehandler og behandler oplysningerne efter instruks fra dem. Vil
                du gøre brug af dine rettigheder, skal du derfor henvende dig til din
                arbejdsgiver.
            </p>
            <p>
                Spørgsmål til selve behandlingen kan sendes til{" "}
                <strong>
                    <a href="mailto:info@cleany.dk">info@cleany.dk</a>
                </strong>
                , som videresender til den dataansvarlige, hvis det er dem, der skal
                svare.
            </p>

            <h2>Hvad vi behandler</h2>

            <h3>Din konto</h3>
            <p>
                Når du logger ind første gang, oprettes en chatkonto ud fra dine
                oplysninger i Cleany. Vi gemmer:
            </p>
            <ul>
                <li>
                    <strong>Et brugernavn uden dit navn i.</strong> Det udledes af din
                    bruger-ID i Cleany og ser ud som <code>u_507676d428c8</code>. Det
                    afslører hverken navn eller e-mail.
                </li>
                <li>
                    <strong>Dit navn</strong>, som det står i Cleany, så kolleger kan se
                    hvem der skriver.
                </li>
                <li>
                    <strong>Din e-mailadresse.</strong>
                </li>
                <li>
                    <strong>Hvilken virksomhed du hører til</strong>, så du kun kan se og
                    skrive med personer i samme organisation.
                </li>
            </ul>

            <h3>Dine beskeder</h3>
            <p>
                Beskeder, billeder og filer, du sender i chatten, gemmes på Cleanys
                chatserver, indtil de slettes.
            </p>
            <p>
                <strong>Samtalerne er ikke end-to-end-krypterede.</strong> Det betyder,
                at Cleany teknisk set kan tilgå indholdet. Det er et bevidst valg:
                chatten er et arbejdsredskab, og samtaler om en opgave skal kunne
                fremfindes og dokumenteres på linje med andre oplysninger i systemet.
            </p>
            <p>
                Adgangen er begrænset til medarbejdere hos Cleany med driftsansvar, og
                bruges til drift, fejlsøgning og dokumentation — ikke til at følge med i
                samtaler.
            </p>
            <p>
                Cleany åbner kun indholdet af en samtale, når det er nødvendigt for
                drift eller fejlsøgning, eller når den dataansvarlige beder om det.
                Adgangen er begrænset til navngivne medarbejdere, og den logges.
            </p>

            <h3>Hvem der kan se hvad</h3>
            <p>
                Grupperne oprettes automatisk ud fra opgaverne i Cleany. Du kommer med i
                en gruppe, fordi du er tilknyttet den opgave — ikke fordi nogen inviterer
                dig.
            </p>
            <ul>
                <li>
                    <strong>Interne grupper</strong> er kun for opgaveansvarlig,
                    kundeansvarlig og det personale, der udfører arbejdet. Kunden kan
                    ikke se dem.
                </li>
                <li>
                    <strong>Eksterne grupper</strong> indeholder desuden de
                    kundekontakter, der er valgt på opgaven.
                </li>
            </ul>
            <p>
                Organisationer kan ikke se hinanden. Du kan hverken finde eller skrive
                til brugere uden for din egen organisation.
            </p>

            <h3>Notifikationer</h3>
            <p>
                For at kunne give dig besked om nye beskeder sender vi en notifikation
                via Apple (APNs) eller Google (FCM). Vi gemmer en enhedsnøgle fra Apple
                eller Google til det formål.
            </p>
            <p>
                På iPhone indeholder notifikationen ikke selve beskeden — kun at der er
                noget nyt, og i hvilken samtale. Beskedens indhold hentes først, når du
                åbner appen.
            </p>
            <p>
                Apple og Google behandler notifikationen på vegne af os for at kunne
                levere den til din telefon.
            </p>

            <h3>På din telefon</h3>
            <p>
                Appen gemmer en kopi af dine samtaler lokalt, så du kan læse dem uden
                forbindelse. Den kopi er krypteret på enheden. Sletter du appen, slettes
                den med.
            </p>

            <h3>Tekniske logfiler</h3>
            <p>
                Serveren logger tidspunkter, IP-adresser og fejl, som enhver server gør.
                Det bruges til drift og sikkerhed.
            </p>
            <p>
                Logfilerne roteres automatisk og overskrives løbende. De gemmes ikke som
                historik, men kun så længe de er nødvendige for at kunne undersøge en
                nylig fejl — i praksis dage til uger, afhængigt af aktiviteten.
            </p>

            <h2>Hvor det ligger</h2>
            <p>
                Chatserveren står hos Hetzner i <strong>Finland</strong>, altså inden for
                EU/EØS. Data forlader ikke EU/EØS, bortset fra notifikationer, der
                nødvendigvis passerer Apple og Google.
            </p>
            <p>
                Der er indgået databehandleraftale med vores serverudbyder.
                Notifikationer leveres af Apple og Google, som behandler dem på vores
                vegne.
            </p>

            <h2>Hvor længe vi gemmer det</h2>
            <p>
                Beskeder gemmes, så længe de har betydning for det arbejde, de handler
                om.
            </p>
            <p>
                Vi sletter ikke automatisk efter et bestemt antal måneder. En samtale om
                en opgave kan være nødvendig at kunne fremfinde længe efter, opgaven er
                afsluttet — for eksempel hvis der opstår uenighed om, hvad der blev
                aftalt.
            </p>
            <p>
                Beskeder slettes, når de ikke længere tjener det formål, eller når din
                arbejdsgiver eller kunden beder om det. Deaktiveres en gruppe i Cleany,
                forsvinder den fra deltagernes app, men samtalen bliver stående på
                serveren, indtil den slettes.
            </p>
            <p>Din konto lukkes, når din adgang til Cleany ophører.</p>

            <h2>Dine rettigheder</h2>
            <p>
                Du har ret til at få indsigt i, rettet og slettet dine oplysninger, og
                til at gøre indsigelse mod behandlingen. Henvend dig til din
                arbejdsgiver, som er dataansvarlig. Cleany sletter efter deres instruks.
            </p>
            <p>
                Bemærk at chatten er et arbejdsredskab. En samtale involverer flere, og
                enkelte beskeder kan ikke altid fjernes uden at gøre samtalen ubrugelig
                som dokumentation for de øvrige. Den dataansvarlige vurderer det
                konkret.
            </p>

            <h2>Ændringer</h2>
            <p>
                Vi opdaterer denne politik, når systemet ændrer sig væsentligt. Siden
                blev sidst ændret <strong>28. august 2026</strong>.
            </p>
        </PolicyLayout>
    );
}
